import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const ComputerVision = () => {
  return (
    <>
      <SEO 
        title="Computer Vision Solutions - AI-Powered Visual Intelligence"
        description="Transform visual data into actionable insights with Abacies Logiciels' enterprise computer vision solutions. Object detection, image recognition, facial analysis, and visual inspection powered by cutting-edge AI."
        keywords="computer vision, image recognition, object detection, visual AI, facial recognition, OCR, video analytics, quality inspection, deep learning vision"
      />

      {/* Hero Section */}
      <section className="relative pt-20 md:pt-24 pb-16 md:pb-20 bg-gradient-to-br from-pearl-white via-white to-blush-light/10 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #E63946 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="page-container relative">
          <div className="max-w-4xl mx-auto text-center">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center space-x-2 text-sm text-titanium mb-6 animate-fade-in">
              <Link to="/" className="hover:text-primary-red transition-colors">Home</Link>
              <span>/</span>
              <Link to="/ai-hub" className="hover:text-primary-red transition-colors">AI Hub</Link>
              <span>/</span>
              <span className="text-charcoal">Computer Vision</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              Computer Vision <span className="text-gradient">Solutions</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up">
              Transform visual data into actionable business intelligence with enterprise-grade computer vision powered by advanced deep learning and neural networks.
            </p>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm">
                <div className="text-3xl font-bold text-primary-red mb-1">99.8%</div>
                <div className="text-sm text-titanium">Accuracy Rate</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm">
                <div className="text-3xl font-bold text-primary-red mb-1">&lt;100ms</div>
                <div className="text-sm text-titanium">Processing Time</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm">
                <div className="text-3xl font-bold text-primary-red mb-1">50M+</div>
                <div className="text-sm text-titanium">Images Analyzed</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm">
                <div className="text-3xl font-bold text-primary-red mb-1">24/7</div>
                <div className="text-sm text-titanium">Real-time Processing</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="btn-primary">
                Request Demo
              </Link>
              <Link to="/ai-hub" className="btn-secondary">
                Explore AI Hub
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is Computer Vision */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                  What is Computer Vision?
                </h2>
                <p className="text-titanium mb-4">
                  Computer vision is an AI technology that enables machines to interpret and understand visual information from the world, mimicking human visual perception. Our solutions leverage deep learning neural networks to process images and videos, extracting meaningful insights and automating visual tasks at scale.
                </p>
                <p className="text-titanium mb-6">
                  From quality inspection in manufacturing to facial recognition in security systems, computer vision transforms how businesses operate by automating visual analysis, detecting patterns invisible to the human eye, and processing visual data at speeds impossible for human operators.
                </p>
                <div className="space-y-3">
                  {[
                    'Real-time object detection and tracking',
                    'Image classification and categorization',
                    'Facial recognition and biometric analysis',
                    'Optical Character Recognition (OCR)',
                    'Video analytics and motion detection',
                    'Visual quality inspection and defect detection'
                  ].map((item, index) => (
                    <div key={index} className="flex items-start">
                      <svg className="w-6 h-6 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-graphite">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-blush-light/30 to-coral-mist/20 rounded-2xl p-8 backdrop-blur-sm border border-platinum">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-platinum hover:shadow-lg transition-all duration-300">
                      <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center mb-4">
                        <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-charcoal mb-2">Object Detection</h3>
                      <p className="text-sm text-titanium">Identify and locate objects in images and videos</p>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-platinum hover:shadow-lg transition-all duration-300">
                      <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center mb-4">
                        <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-charcoal mb-2">Image Analysis</h3>
                      <p className="text-sm text-titanium">Extract insights from visual content</p>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-platinum hover:shadow-lg transition-all duration-300">
                      <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center mb-4">
                        <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-charcoal mb-2">Video Analytics</h3>
                      <p className="text-sm text-titanium">Real-time video stream processing</p>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-platinum hover:shadow-lg transition-all duration-300">
                      <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center mb-4">
                        <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-charcoal mb-2">OCR & Text</h3>
                      <p className="text-sm text-titanium">Extract text from images and documents</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Core Computer Vision Capabilities
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              Enterprise-grade visual intelligence powered by state-of-the-art deep learning models
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                ),
                title: 'Object Detection & Tracking',
                description: 'Identify, classify, and track multiple objects in real-time across image sequences and video streams with industry-leading accuracy.',
                features: ['Multi-object tracking', 'Real-time detection', '100+ object categories', 'Custom model training']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                ),
                title: 'Facial Recognition',
                description: 'Advanced facial analysis including detection, recognition, emotion analysis, and demographic classification for security and personalization.',
                features: ['Identity verification', 'Emotion detection', 'Age/gender estimation', 'Liveness detection']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                ),
                title: 'OCR & Document Processing',
                description: 'Extract text from images, documents, and scenes with support for multiple languages, handwriting, and complex layouts.',
                features: ['Multi-language support', 'Handwriting recognition', 'Table extraction', 'Form processing']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                ),
                title: 'Quality Inspection',
                description: 'Automated visual inspection for defect detection, quality control, and compliance verification in manufacturing and production.',
                features: ['Defect detection', 'Dimensional analysis', 'Surface inspection', 'Compliance verification']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                ),
                title: 'Image Segmentation',
                description: 'Pixel-level image analysis to identify and separate objects, backgrounds, and regions of interest with semantic and instance segmentation.',
                features: ['Semantic segmentation', 'Instance segmentation', 'Medical imaging', 'Autonomous systems']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                ),
                title: 'Video Analytics',
                description: 'Comprehensive video analysis including activity recognition, crowd monitoring, anomaly detection, and behavioral analysis.',
                features: ['Activity recognition', 'Crowd analytics', 'Anomaly detection', 'Traffic monitoring']
              }
            ].map((capability, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-platinum group">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-red/10 to-coral-mist/10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {capability.icon}
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red transition-colors">
                  {capability.title}
                </h3>
                <p className="text-titanium mb-4">
                  {capability.description}
                </p>
                <div className="space-y-2">
                  {capability.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm text-graphite">
                      <div className="w-1.5 h-1.5 bg-primary-red rounded-full mr-2"></div>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Industry Applications
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              Computer vision transforming operations across industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                industry: 'Manufacturing',
                icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
                applications: [
                  'Automated quality inspection and defect detection',
                  'Assembly line monitoring and process optimization',
                  'Inventory management through visual recognition',
                  'Safety compliance and PPE detection'
                ]
              },
              {
                industry: 'Retail & E-commerce',
                icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
                applications: [
                  'Visual product search and recommendations',
                  'Shelf monitoring and planogram compliance',
                  'Cashierless checkout systems',
                  'Customer behavior and heat mapping analysis'
                ]
              },
              {
                industry: 'Healthcare',
                icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
                applications: [
                  'Medical imaging analysis (X-ray, MRI, CT scans)',
                  'Disease detection and diagnostic assistance',
                  'Patient monitoring and fall detection',
                  'Surgical assistance and guidance systems'
                ]
              },
              {
                industry: 'Security & Surveillance',
                icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
                applications: [
                  'Facial recognition for access control',
                  'Suspicious behavior and anomaly detection',
                  'License plate recognition (ANPR/LPR)',
                  'Crowd monitoring and people counting'
                ]
              },
              {
                industry: 'Automotive',
                icon: 'M13 10V3L4 14h7v7l9-11h-7z',
                applications: [
                  'Autonomous vehicle perception systems',
                  'Driver monitoring and drowsiness detection',
                  'Traffic sign recognition and lane detection',
                  'Parking assistance and collision avoidance'
                ]
              },
              {
                industry: 'Agriculture',
                icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                applications: [
                  'Crop health monitoring and disease detection',
                  'Automated harvesting and sorting',
                  'Livestock monitoring and counting',
                  'Precision agriculture and yield prediction'
                ]
              }
            ].map((industry, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-blush-light/20 rounded-xl p-6 border border-platinum hover:shadow-lg transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={industry.icon} />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-charcoal mb-1">{industry.industry}</h3>
                  </div>
                </div>
                <ul className="space-y-2">
                  {industry.applications.map((app, idx) => (
                    <li key={idx} className="flex items-start text-graphite">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm">{app}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                Advanced Technology Stack
              </h2>
              <p className="text-xl text-titanium">
                Built on industry-leading frameworks and architectures
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-platinum">
                <h3 className="font-semibold text-charcoal mb-4">Deep Learning Frameworks</h3>
                <div className="space-y-2 text-sm text-titanium">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    TensorFlow & Keras
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    PyTorch & Torchvision
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    ONNX Runtime
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    OpenCV & scikit-image
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-platinum">
                <h3 className="font-semibold text-charcoal mb-4">Neural Network Architectures</h3>
                <div className="space-y-2 text-sm text-titanium">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    YOLO v8 & v9 (Object Detection)
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    ResNet & EfficientNet
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    Mask R-CNN (Segmentation)
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    Vision Transformers (ViT)
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-platinum">
                <h3 className="font-semibold text-charcoal mb-4">Infrastructure & Deployment</h3>
                <div className="space-y-2 text-sm text-titanium">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    NVIDIA CUDA & TensorRT
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    Docker & Kubernetes
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    AWS, Azure, GCP
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-primary-red rounded-full mr-2"></div>
                    Edge AI & IoT Integration
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                Business Benefits
              </h2>
              <p className="text-xl text-titanium">
                Transform operations with visual intelligence
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: 'Increased Efficiency',
                  description: 'Automate visual inspection tasks, reducing processing time by up to 90% compared to manual methods.',
                  metric: '90% faster'
                },
                {
                  title: 'Enhanced Accuracy',
                  description: 'Achieve 99%+ accuracy in object detection and classification, eliminating human error.',
                  metric: '99.8% accuracy'
                },
                {
                  title: 'Cost Reduction',
                  description: 'Lower operational costs through automation, reducing labor requirements and improving throughput.',
                  metric: '60% cost savings'
                },
                {
                  title: '24/7 Operations',
                  description: 'Continuous monitoring and analysis without fatigue, enabling round-the-clock quality control.',
                  metric: '100% uptime'
                },
                {
                  title: 'Scalability',
                  description: 'Process millions of images and video streams simultaneously with cloud-based infrastructure.',
                  metric: 'Unlimited scale'
                },
                {
                  title: 'Real-time Insights',
                  description: 'Instant visual analysis and alerts enabling immediate decision-making and response.',
                  metric: '<100ms latency'
                }
              ].map((benefit, index) => (
                <div key={index} className="bg-gradient-to-br from-white to-blush-light/10 rounded-xl p-6 border border-platinum hover:shadow-lg transition-all duration-300">
                  <div className="text-2xl font-bold text-primary-red mb-2">{benefit.metric}</div>
                  <h3 className="text-lg font-semibold text-charcoal mb-2">{benefit.title}</h3>
                  <p className="text-sm text-titanium">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                Implementation Process
              </h2>
              <p className="text-xl text-titanium">
                Structured approach to deploying computer vision solutions
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  step: '01',
                  title: 'Discovery & Assessment',
                  description: 'Analyze use cases, data requirements, and success metrics'
                },
                {
                  step: '02',
                  title: 'Data Collection & Preparation',
                  description: 'Gather and annotate training data, establish data pipelines'
                },
                {
                  step: '03',
                  title: 'Model Development',
                  description: 'Train and optimize custom models for your specific requirements'
                },
                {
                  step: '04',
                  title: 'Deployment & Scaling',
                  description: 'Deploy to production, integrate with systems, monitor performance'
                }
              ].map((phase, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-platinum hover:shadow-lg transition-all duration-300">
                    <div className="text-4xl font-bold text-primary-red/20 mb-3">{phase.step}</div>
                    <h3 className="text-lg font-semibold text-charcoal mb-2">{phase.title}</h3>
                    <p className="text-sm text-titanium">{phase.description}</p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-primary-red to-deep-garnet text-white">
        <div className="page-container text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Transform Visual Data into Intelligence?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Discover how computer vision can revolutionize your operations. Schedule a consultation with our AI experts to explore custom solutions for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
                Schedule Consultation
              </Link>
              <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
                Explore AI Solutions
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-charcoal mb-4">Related AI Capabilities</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/ai-hub/nlp" className="card hover:shadow-xl transition-all duration-300 group">
              <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red transition-colors">
                Natural Language Processing
              </h3>
              <p className="text-titanium mb-4">Extract insights from text data with advanced NLP</p>
              <span className="text-primary-red font-semibold">Learn More →</span>
            </Link>
            <Link to="/ai-hub/predictive-analytics" className="card hover:shadow-xl transition-all duration-300 group">
              <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red transition-colors">
                Predictive Analytics
              </h3>
              <p className="text-titanium mb-4">Forecast trends and outcomes with ML models</p>
              <span className="text-primary-red font-semibold">Learn More →</span>
            </Link>
            <Link to="/ai-hub/intelligent-automation" className="card hover:shadow-xl transition-all duration-300 group">
              <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red transition-colors">
                Intelligent Automation
              </h3>
              <p className="text-titanium mb-4">Automate complex processes with AI-powered workflows</p>
              <span className="text-primary-red font-semibold">Learn More →</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ComputerVision;